// Airports and flight paths (Section 2 continued), as in the report (10-serverless/120-pack/render.py
// s2d, drawn by pack_maps.aviation_svg): on every property, even when the nearest airport is far away,
// with the working shown (Neil, 11 Oct 2026). Why airports matter for solar, the headline figures, a
// map of the nearest airports with their runways and 20 km approach areas, the table of airports, how
// we checked, small airstrips, and the result. From the API's site.extras.airports (100-api
// common/site_extras.py airports()). Never names where the airfield data comes from.
import { useEffect, useMemo, useState, type ReactElement } from "react";
import { BadgeCheck, Info, Plane } from "lucide-react";
import type { AirportCheck, AirportResult, OwnedBuildingDetail, SiteExtras } from "../api";
import { SectionHead } from "./PackSections";
import { g } from "./packFormat";
import { Labels, MAP, PIN_PATH, Proj, districtCode, loadMapData, nationOf, ringsPath, textWidth, type MapData } from "./svgMap";

type Aviation = NonNullable<SiteExtras["airports"]>;

// The result as the report's figure and table say it; flagged ones highlighted
const RESULT: Record<AirportResult, [string, boolean]> = {
  "final-approach": ["Under a final approach", true],
  "under-approach": ["Under an approach path", true],
  "within-distance": ["Close to an airport", true],
  clear: ["Clear", false],
};
const KIND: Record<string, string> = { airport: "Airport", military: "Military airfield" };

// The approach area's shape, as site_extras.py and pack_maps.py: 300 m wide (150 m each side) starting
// 60 m out from the runway end, widening by the splay each side over the approach
const INNER_HALF_M = 150;
const INNER_FROM_M = 60;
const APPROACH_BLUE = "#2f6fb0";
const CHECK_AMBER = "#c27a00";

/** A landing aircraft's height this far out: the glide path plus 50 ft over the runway end, to the nearest 10 ft. */
export const glideFt = (alongM: number, glideDeg = 3.0) => Math.round((alongM * Math.tan((glideDeg * Math.PI) / 180) * 3.281 + 50) / 10) * 10;

/** The point `metres` from lat/lng on `bearing` (flat-earth: fine over 20-30 km). */
function move(lat: number, lng: number, bearing: number, metres: number): [number, number] {
  const b = (bearing * Math.PI) / 180;
  return [lat + (metres * Math.cos(b)) / 111_320, lng + (metres * Math.sin(b)) / (111_320 * Math.cos((lat * Math.PI) / 180))];
}

/** Where the building sits against the airport's nearest approach, in words (render.py _closest_text). */
function closestText(a: AirportCheck) {
  const c = a.closest;
  if (!c) return <>No runway's last 20 km of approach comes near the building.</>;
  return (
    <>
      Runway {c.runway}, aircraft landing from the {c.from}: the building is level with a point {c.alongKm} km out,{" "}
      {c.inside ? (
        <>
          <b>inside</b> the approach area ({g(c.halfWidthKm * 2)} km wide there)
        </>
      ) : (
        <>
          {c.offsetKm} km to the side of the approach line (the area is {g(c.halfWidthKm * 2)} km wide there)
        </>
      )}
      ; landing aircraft are about {Math.trunc(c.heightFt).toLocaleString("en-GB")} ft up at that point.{c.approx ? " Runway position approximate." : ""}
    </>
  );
}

/** Each runway's two end ids ("09/27"), or "Not known"; "(approximate)" when every runway's position is estimated. */
function runwaysText(a: AirportCheck) {
  const rws = a.runways ?? [];
  if (!rws.length) return "Not known";
  return rws.map((r) => r.ends.map((e) => e.id).join("/")).join(", ") + (rws.every((r) => r.approx) ? " (approximate)" : "");
}

/** The map (pack_maps.aviation_svg): the building, the nearest airports, runways, approach areas, 10 km rings, to scale. */
function AviationMap({ lat, lng, av, data }: { lat: number; lng: number; av: Aviation; data: MapData }) {
  const W = 640,
    H = 430;
  const svg = useMemo(() => {
    const rules = av.rules;
    const approachM = (rules?.approachKm || 20) * 1000;
    const splay = (rules?.splayPct || 15) / 100;
    const assessM = (rules?.assessKm || 10) * 1000;
    const glide = rules?.glideDeg || 3.0;
    const shown = av.airports ?? [];
    const cosLat = Math.cos((lat * Math.PI) / 180);
    // The extent: the building and every airport shown, with room for their approaches; at least 25 km round the building
    const padLat = 12 / 111.32;
    const padLng = padLat / cosLat;
    const lats = [lat, ...shown.map((a) => a.lat)];
    const lngs = [lng, ...shown.map((a) => a.lng)];
    const south = Math.min(Math.min(...lats) - padLat, lat - 25 / 111.32);
    const north = Math.max(Math.max(...lats) + padLat, lat + 25 / 111.32);
    const west = Math.min(Math.min(...lngs) - padLng, lng - 25 / (111.32 * cosLat));
    const east = Math.max(Math.max(...lngs) + padLng, lng + 25 / (111.32 * cosLat));
    const pr = new Proj(south, west, north, east, W, H);
    const [px, py] = pr.xy(lat, lng);
    const kmPx = (pr.k * pr.s) / (111.32 * cosLat); // pixels per km
    // Kept clear of labels: the pin, the scale bar, the bottom-left corner
    const labels = new Labels(W, H, [
      [px - 16, py - 46, px + 16, py + 2],
      [W - 120, H - 34, W, H],
      [0, H - 40, 260, H],
    ]);
    const under: ReactElement[] = []; // rings and approach areas, under the marks
    const marks: ReactElement[] = [];
    const texts: ReactElement[] = [];
    const label = (key: string, x: number, y: number, text: string, size: number, weight: number, colour: string, anchor: "start" | "middle" | "end" = "start") => (
      <text
        key={key}
        x={x}
        y={y}
        fontSize={size}
        fontWeight={weight}
        fill={colour}
        textAnchor={anchor}
        fontFamily="Manrope, sans-serif"
        stroke="#fff"
        strokeWidth={3}
        strokeLinejoin="round"
        paintOrder="stroke"
      >
        {text}
      </text>
    );

    // Cities and large towns, faint, so the reader can place it
    [...data.places.places]
      .filter((p) => p.rank <= 2)
      .sort((a, b) => a.rank - b.rank)
      .forEach((p) => {
        const [x, y] = pr.xy(p.lat, p.lng);
        if (x > 0 && x < W && y > 0 && y < H && labels.fits(x + 5, y - 9, x + 5 + textWidth(p.name, 11), y + 3)) {
          marks.push(<circle key={`pd-${p.name}`} cx={x} cy={y} r={2.6} fill="#8e959e" />);
          texts.push(label(`pl-${p.name}`, x + 5, y + 3.5, p.name, 11, 600, "#6b7280"));
        }
      });

    shown.forEach((a, i) => {
      const [ax, ay] = pr.xy(a.lat, a.lng);
      // The distance within which the airport may ask for a glare check
      under.push(
        <circle key={`ring-${i}`} cx={ax} cy={ay} r={(assessM / 1000) * kmPx} fill={CHECK_AMBER} fillOpacity={0.05} stroke={CHECK_AMBER} strokeWidth={1.2} strokeDasharray="5 4" />,
      );
      (a.runways ?? []).forEach((r, ri) => {
        r.ends.forEach((end, ei) => {
          // The approach area: aircraft come in from the opposite of the landing heading
          const back = ((end.headingT ?? 0) + 180) % 360;
          const farHalf = INNER_HALF_M + splay * (approachM - INNER_FROM_M);
          const corners = [
            move(...move(end.lat, end.lng, back, INNER_FROM_M), back + 90, INNER_HALF_M),
            move(...move(end.lat, end.lng, back, approachM), back + 90, farHalf),
            move(...move(end.lat, end.lng, back, approachM), back - 90, farHalf),
            move(...move(end.lat, end.lng, back, INNER_FROM_M), back - 90, INNER_HALF_M),
          ];
          const pts = corners.map(([la, ln]) => pr.xy(la, ln).map((v) => v.toFixed(1)).join(",")).join(" ");
          const key = `${i}-${ri}-${ei}`;
          under.push(
            <polygon key={`ap-${key}`} points={pts} fill={APPROACH_BLUE} fillOpacity={0.1} stroke={APPROACH_BLUE} strokeWidth={0.8} strokeOpacity={0.55} />,
          );
          const [x1, y1] = pr.xy(end.lat, end.lng);
          const [x2, y2] = pr.xy(...move(end.lat, end.lng, back, approachM));
          under.push(<line key={`cl-${key}`} x1={x1} y1={y1} x2={x2} y2={y2} stroke={APPROACH_BLUE} strokeWidth={0.9} strokeDasharray="3 3" />);
          // Heights along the nearest airport's approaches: where a landing aircraft would be
          if (i === 0) {
            [5, 10, 20].forEach((km) => {
              const [tx, ty] = pr.xy(...move(end.lat, end.lng, back, km * 1000));
              marks.push(<circle key={`hd-${key}-${km}`} cx={tx} cy={ty} r={2.2} fill={APPROACH_BLUE} />);
              const t = `${glideFt(km * 1000, glide).toLocaleString("en-GB")} ft`;
              if (labels.fits(tx + 4, ty - 8, tx + 4 + textWidth(t, 9.5), ty + 3)) texts.push(label(`ht-${key}-${km}`, tx + 4, ty + 3, t, 9.5, 700, APPROACH_BLUE));
            });
          }
        });
        // The runway itself
        if (r.ends.length >= 2) {
          const [x1, y1] = pr.xy(r.ends[0].lat, r.ends[0].lng);
          const [x2, y2] = pr.xy(r.ends[1].lat, r.ends[1].lng);
          marks.push(<line key={`rw-${i}-${ri}`} x1={x1} y1={y1} x2={x2} y2={y2} stroke={MAP.navy} strokeWidth={3.2} strokeLinecap="round" />);
        }
      });
      marks.push(<circle key={`ad-${i}`} cx={ax} cy={ay} r={4.2} fill="#fff" stroke={MAP.navy} strokeWidth={1.6} />);
      // The name above-right, above-left, below-right or below-left: wherever it fits first
      const tw = textWidth(a.name, 12);
      for (const [dx, dy, anchor] of [
        [8, -7, "start"],
        [-8, -7, "end"],
        [8, 16, "start"],
        [-8, 16, "end"],
      ] as const) {
        const x1 = anchor === "start" ? ax + dx : ax + dx - tw;
        if (labels.fits(x1, ay + dy - 11, x1 + tw, ay + dy + 2)) {
          texts.push(label(`an-${i}`, ax + dx, ay + dy, a.name, 12, 800, MAP.navy, anchor));
          break;
        }
      }
    });

    // The building to the nearest airport, with the distance
    if (shown.length) {
      const [ax, ay] = pr.xy(shown[0].lat, shown[0].lng);
      under.push(<line key="to-nearest" x1={px} y1={py} x2={ax} y2={ay} stroke={MAP.navy} strokeWidth={1.2} strokeDasharray="2 3" />);
      texts.push(label("to-nearest-km", (px + ax) / 2, (py + ay) / 2 - 4, `${shown[0].km} km`, 12, 800, MAP.navy, "middle"));
    }
    return { pr, px, py, kmPx, under, marks, texts };
  }, [lat, lng, av, data]);

  const { pr, px, py, kmPx, under, marks, texts } = svg;
  return (
    <svg viewBox={`0 0 ${W} ${H}`} width="100%" role="img" aria-label="Airports, runways and approach paths near the building">
      <rect width={W} height={H} fill={MAP.sea} />
      {(data.context?.features ?? []).map((f, i) => (
        <path key={`c${i}`} d={ringsPath(f.geometry, pr)} fill={MAP.land} stroke={MAP.edge} strokeWidth={0.6} />
      ))}
      {data.councils.features.map((f, i) => (
        <path key={`d${i}`} d={ringsPath(f.geometry, pr)} fill="none" stroke={MAP.districtEdge} strokeWidth={0.4} strokeOpacity={0.6} />
      ))}
      {under}
      {marks}
      {texts}
      {/* The building: the console's pin, its point on the location */}
      <g transform={`translate(${px - 14 * 1.1},${py - 37 * 1.1}) scale(1.1)`}>
        <path d={PIN_PATH} fill={MAP.lime} stroke={MAP.navy} strokeWidth={1.5} />
        <circle cx={14} cy={13.5} r={4.5} fill={MAP.navy} />
      </g>
      {/* A scale bar: 10 km */}
      <g transform={`translate(${W - 20 - 10 * kmPx},${H - 18})`}>
        <rect width={10 * kmPx} height={4} fill={MAP.navy} />
        <text x={5 * kmPx} y={-5} fontSize={10} fontWeight={600} fill={MAP.navy} textAnchor="middle" fontFamily="Manrope, sans-serif" stroke="#fff" strokeWidth={3} paintOrder="stroke">
          10 km
        </text>
      </g>
    </svg>
  );
}

/** Section 2 continued: airports and flight paths. */
export function AirportsSection({ b }: { b: OwnedBuildingDetail }) {
  const av = b.site?.extras?.airports;
  const loc = b.property.location;
  const [data, setData] = useState<MapData | null>(null);
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    let cancelled = false;
    loadMapData()
      .then((d) => !cancelled && setData(d))
      .catch(() => !cancelled && setFailed(true));
    return () => {
      cancelled = true;
    };
  }, []);

  const head = (
    <SectionHead
      id="pk-airports"
      kicker="Section 2 continued"
      icon={Plane}
      title="Airports and flight paths"
      sub="Solar panels can reflect sunlight towards pilots and air traffic controllers, so airports can object to solar near them. We check every property against the airports around it: here is that check, so you can see the working."
    />
  );

  // Not worked out yet (the site checks run in the background once), or from before the check showed its working
  if (!b.site?.extras || (av && av.checked !== false && !av.airports)) {
    return (
      <section className="card eo" aria-labelledby="pk-airports">
        {head}
        <p className="pk-notfound">We're checking the airports and flight paths around the building. Refresh in a minute.</p>
      </section>
    );
  }
  if (!av?.airports?.length) {
    return (
      <section className="card eo" aria-labelledby="pk-airports">
        {head}
        <p className="pk-notfound">Airports and flight paths weren't checked for this report.</p>
      </section>
    );
  }

  const rules = av.rules;
  const assessKm = rules?.assessKm ?? 10;
  const approachKm = rules?.approachKm ?? 20;
  const glide = rules?.glideDeg ?? 3.0;
  const ft5 = glideFt(5000, glide);
  const ft20 = glideFt(20000, glide);
  const first = av.airports[0];
  const [resultTitle, resultFlagged] = RESULT[av.result ?? "clear"] ?? ["Checked", false];
  const strips = av.airstrips ?? [];

  // Scotland: no permitted development within 3 km of an aerodrome's boundary. We measure to the
  // airfield's centre, so anything within 5 km is flagged to check. The nation from the tax block
  // (the postcode), else the district's code
  const nation = b.installer?.tax?.nation ?? (data ? nationOf(districtCode(data.councils, b.property.district)) : null);
  const nearScotland = nation === "Scotland" ? av.airports.find((a) => a.km <= 5) : undefined;

  return (
    <section className="card eo" aria-labelledby="pk-airports">
      {head}

      {/* Why airports matter for solar PV, for a reader who's never met the rules (render.py av_why) */}
      <div className="about-list pk-panel">
        <h3>
          <Info size={16} aria-hidden="true" /> Why airports matter for solar PV
        </h3>
        <ul>
          <li>
            <b>Glint and glare.</b> Panels are made to absorb light, and their anti-reflective glass reflects only about 2 to 4% of sunlight, about the same as still
            water and less than ordinary window glass. But at certain sun angles a roof of panels can send a brief flash (glint) or a longer bright reflection
            (glare) in one direction. Near an airport, that direction can be a pilot on the final approach to land, or a controller in the tower.
          </li>
          <li>
            <b>Who decides.</b> The UK's larger airports are officially safeguarded: the local council consults them on developments that could affect flight safety,
            and they can object. UK aviation guidance leaves the distance to each airport: commonly 5 km, and up to {assessKm} km, for solar.
          </li>
          <li>
            <b>What an assessment does.</b> A glint and glare assessment follows the sun through every day of the year and traces the reflections from the panels to
            the approach paths and the control tower. Where it finds a problem, changing the panels' tilt, direction or layout usually solves it.
          </li>
          <li>
            <b>The track record.</b> The UK's aviation regulator has reported no glare problems from established solar sites, including some right next to
            airfields, while asking safeguarding teams to stay alert. A check gives the evidence; it is rarely a reason not to install.
          </li>
          <li>
            <b>Planning.</b> In England, rooftop solar on non-domestic buildings is usually permitted development, with no exclusion near airports (the limits are on
            height, distance from the roof edge, listed buildings and monuments). In Scotland, permitted development doesn't apply within 3 km of an aerodrome, so a
            planning application is needed there. Wales and Northern Ireland have their own rules. Even where no application is needed, checking first avoids
            problems after the panels are up.
          </li>
        </ul>
      </div>

      {/* The headline figures */}
      <div className="eo-stats">
        <div>
          <b>{first.km} km</b>
          <span>to the nearest airport, {first.name}</span>
        </div>
        <div className={resultFlagged ? "is-dark" : undefined}>
          <b>{resultTitle}</b>
          <span>the result of this check</span>
        </div>
        <div>
          <b>{assessKm} km</b>
          <span>within which airports may ask for a glare check</span>
        </div>
        <div>
          <b>{approachKm} km</b>
          <span>of each runway approach checked</span>
        </div>
      </div>

      {/* The map, drawn here as the report draws it */}
      {loc && (
        <figure className="av-map">
          {data ? (
            <div className="av-map-frame">
              <AviationMap lat={loc.lat} lng={loc.lng} av={av} data={data} />
            </div>
          ) : (
            <div className="av-map-frame av-map-wait muted">{failed ? "The map couldn't load." : "Loading the map…"}</div>
          )}
          <figcaption className="muted eo-note">
            <b style={{ color: APPROACH_BLUE }}>Blue</b>: each runway's approach area over its last {approachKm} km, with the height of a landing aircraft at 5, 10
            and 20 km out. <b style={{ color: CHECK_AMBER }}>Dashed amber</b>: {assessKm} km from each airport. Positions approximate.
          </figcaption>
        </figure>
      )}

      {/* Every airport checked: scrolls inside the card on a narrow screen */}
      <div className="eo-table-wrap">
        <table className="eo-table is-wrap av-table">
          <thead>
            <tr>
              <th>Airport</th>
              <th>Distance</th>
              <th>Runways</th>
              <th>The closest approach to the building</th>
              <th>Result</th>
            </tr>
          </thead>
          <tbody>
            {av.airports.map((a) => (
              <tr key={a.name}>
                <td>
                  <b>{a.name}</b>
                  <br />
                  <span className="muted">{KIND[a.kind] ?? "Airfield"}</span>
                </td>
                <td>
                  {a.km} km
                  <br />
                  <span className="muted">{a.direction}</span>
                </td>
                <td>{runwaysText(a)}</td>
                <td>{closestText(a)}</td>
                <td>
                  <b>{RESULT[a.result]?.[0] ?? ""}</b>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="about-two">
        <div className="about-list pk-panel">
          <h3>
            <BadgeCheck size={16} aria-hidden="true" /> How we checked
          </h3>
          <ul>
            <li>
              <b>Distance.</b> Airports set a distance within which they expect solar to be checked for glint and glare: commonly 5 km, and up to {assessKm} km. We
              flag any airport within {assessKm} km.
            </li>
            <li>
              <b>Approach paths.</b> Landing aircraft follow the runway's line down a {g(glide)}° glide path: about {ft5.toLocaleString("en-GB")} ft up at 5 km out
              and {ft20.toLocaleString("en-GB")} ft at 20 km. We draw each runway's approach area over its last {approachKm} km, starting 300 m wide at the runway
              and widening by {rules?.splayPct ?? 15}% each side (the standard shape), and check whether the building is inside it. Glare matters most on the final
              approach, the last 5 km, where aircraft are below about {ft5.toLocaleString("en-GB")} ft.
            </li>
            <li>
              <b>The control tower</b> is on the airfield, so the distance check covers it.
            </li>
            <li>
              <b>Not checked:</b> holding patterns and routes between airports, which are thousands of feet up, and departures, which climb away quickly. Glare from a
              roof isn't assessed there.
            </li>
          </ul>
        </div>
        <div className="about-list pk-panel">
          <h3>
            <Plane size={16} aria-hidden="true" /> Small airstrips nearby
          </h3>
          {strips.length ? (
            <ul>
              {strips.map((s) => (
                <li key={s.name}>
                  <b>{s.name}</b>, {s.km} km {s.direction}: a small airstrip with no formal approach paths. Worth telling its operator about the installation.
                </li>
              ))}
            </ul>
          ) : (
            <p className="eo-note">No small airstrips within {rules?.airstripKm ?? 5} km.</p>
          )}
        </div>
      </div>

      {/* The result, with Scotland's 3 km rule where it applies */}
      <div className={`pk-callout${resultFlagged ? " is-amber" : ""}`}>
        <Plane size={16} aria-hidden="true" />
        <span>
          <b>Result:</b> {av.note}
          {nearScotland && (
            <>
              {" "}
              <b>In Scotland, rooftop solar within 3 km of an aerodrome's boundary needs a planning application:</b> {nearScotland.name} is about {nearScotland.km} km
              from here (measured to its centre), so check the distance to its boundary.
            </>
          )}{" "}
          The local planning authority makes the final decision and consults the airport where it needs to.
        </span>
      </div>
    </section>
  );
}
